/**
 * Bundle 80.2 Task 2 - Testimonials carousel.
 * Bundle 164 B - `variant` prop added.
 *
 *   variant="trio"   (default, event detail pages) - shows 3
 *                     cards at a time in a grid, auto-advances
 *                     every 8s.
 *   variant="single" (homepage) - shows 1 card at a time,
 *                     auto-advances every 7s, arrows + dots,
 *                     `prefers-reduced-motion: reduce` disables
 *                     auto-advance and keeps arrows visible.
 *
 * Bundle 164 B also fixes a latent a11y gap on the trio variant
 * by respecting `prefers-reduced-motion` for BOTH variants.
 *
 * Source rule: `status='published'` per Bundle 80.2 schema.
 * In this schema `status='published'` IS the "approved and
 * public" state; the review workflow uses the separate
 * `ai_draft_status` field (Bundle 156b). Do not confuse the two.
 * When mounted on the homepage the carousel calls the Bundle 164
 * endpoint `/testimonials/carousel` which adds an extra
 * hidden-flag guard on top of the Bundle 80.2 filters.
 *
 * If the API returns zero publishable rows the entire section
 * renders NOTHING (no empty state on marketing).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import TestimonialCard from '@/components/TestimonialCard';

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api` });

const AUTO_ADVANCE_TRIO_MS = 8000;
const AUTO_ADVANCE_SINGLE_MS = 7000;

// Media-query safe helper - returns false in SSR / older
// browsers where matchMedia is unavailable. Tests can override
// via the `forceReducedMotion` prop.
const detectReducedMotion = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  try {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
};

const TestimonialsCarousel = ({
  variant = 'trio',
  endpoint = '/testimonials/featured',
  params = { limit: 6 },
  fallbackEndpoint = '/testimonials/random',
  fallbackParams = { limit: 6 },
  eyebrow = 'What our guests say',
  headline = 'Real gatherings, in their own words.',
  ctaHref = '/testimonials',
  ctaLabel = 'Read more',
  forceReducedMotion = null, // testing hook
}) => {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const reducedMotion = forceReducedMotion !== null
    ? forceReducedMotion
    : detectReducedMotion();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params });
      let fetched = data?.testimonials || [];
      if (fetched.length < (params?.limit || 6) && fallbackEndpoint) {
        try {
          const { data: d2 } = await api.get(fallbackEndpoint, {
            params: fallbackParams,
          });
          const extra = (d2?.testimonials || []).filter(
            (t) => !fetched.some((f) => f.id === t.id),
          );
          fetched = [...fetched, ...extra].slice(0, params?.limit || 6);
        } catch (_) {
          // Fallback failure is non-blocking.
        }
      }
      setItems(fetched);
      setIndex(0);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, JSON.stringify(params), fallbackEndpoint, JSON.stringify(fallbackParams)]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (reducedMotion) return undefined; // a11y: no autoplay
    if (paused || items.length <= 1) return undefined;
    const ms = variant === 'single'
      ? AUTO_ADVANCE_SINGLE_MS : AUTO_ADVANCE_TRIO_MS;
    timerRef.current = window.setTimeout(() => {
      setIndex((i) => (i + 1) % items.length);
    }, ms);
    return () => timerRef.current && window.clearTimeout(timerRef.current);
  }, [index, paused, items.length, reducedMotion, variant]);

  const trio = useMemo(() => {
    if (items.length === 0) return [];
    // Trio variant only. Wraps around at the end.
    const out = [];
    for (let k = 0; k < Math.min(3, items.length); k += 1) {
      out.push(items[(index + k) % items.length]);
    }
    return out;
  }, [items, index]);

  // Hide entirely when nothing is publishable.
  if (!loading && items.length === 0) return null;

  const single = variant === 'single';
  const showControls = items.length > (single ? 1 : 3);
  const activeItem = items[index];

  return (
    <section
      className="bg-white py-16 px-4 sm:px-6"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonials-carousel"
      data-variant={variant}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
    >
      <div className={single ? 'max-w-3xl mx-auto' : 'max-w-6xl mx-auto'}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-secondary-800">{eyebrow}</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary-900 mt-2">
              {headline}
            </h2>
          </div>
          {ctaHref ? (
            <a
              href={ctaHref}
              className="text-secondary-800 hover:text-secondary-600 text-sm font-medium underline underline-offset-4"
              data-testid="testimonials-carousel-cta"
            >
              {ctaLabel}
            </a>
          ) : null}
        </div>

        {loading ? (
          <div className="text-neutral-500 text-sm">Loading warm stories...</div>
        ) : (
          <div className="relative">
            {single ? (
              <div
                className="grid grid-cols-1"
                data-testid="testimonials-carousel-single-slot"
                aria-live={reducedMotion ? 'polite' : 'off'}
              >
                {activeItem ? (
                  <TestimonialCard key={activeItem.id} t={activeItem} />
                ) : null}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {trio.map((t) => <TestimonialCard key={t.id} t={t} />)}
              </div>
            )}

            {showControls ? (
              <>
                <div className="flex justify-center gap-2 mt-6">
                  {items.map((t, i) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setIndex(i)}
                      className={`h-2 w-2 rounded-full transition-colors ${
                        i === index ? 'bg-primary-900' : 'bg-neutral-300'
                      }`}
                      aria-label={`Testimonial ${i + 1} of ${items.length}`}
                      data-testid={`carousel-dot-${i}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setIndex((i) => (i - 1 + items.length) % items.length)}
                  className="absolute -left-3 top-1/2 -translate-y-1/2 bg-white border border-neutral-200 rounded-full h-9 w-9 flex items-center justify-center hover:bg-neutral-50 shadow-sm"
                  aria-label="Previous testimonial"
                  data-testid="carousel-prev-btn"
                >
                  <ChevronLeft className="h-4 w-4 text-primary-900" />
                </button>
                <button
                  type="button"
                  onClick={() => setIndex((i) => (i + 1) % items.length)}
                  className="absolute -right-3 top-1/2 -translate-y-1/2 bg-white border border-neutral-200 rounded-full h-9 w-9 flex items-center justify-center hover:bg-neutral-50 shadow-sm"
                  aria-label="Next testimonial"
                  data-testid="carousel-next-btn"
                >
                  <ChevronRight className="h-4 w-4 text-primary-900" />
                </button>
              </>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
